import { useEffect, useRef, useState } from 'react';
import { ItemArt } from '../avatar/itemArt';
import { Bubble } from '../components/Bubble';
import { HuntBubble } from '../components/HuntBubble';
import { RarityBadge } from '../components/RarityBadge';
import { TimeLeftLabel } from '../components/TimeLeftLabel';
import { ApiException } from '../services/apiClient';
import { POOR_SIGNAL_MESSAGE } from '../services/messages';
import { type DropView, type FindView, type ResolveResult, activeHunts, liveDrops, rarityLabel, rarityWeight } from '../models/types';
import { catalog } from '../state/catalog';
import { usePlayer } from '../state/player';
import { cooldownRemaining, isBusy, pickupMessage, useScan } from '../state/scan';
import { useShell } from '../state/shell';
import { colors, rarityColors, withAlpha } from '../theme';
import { FindResultSheet } from './FindResultSheet';

/** How often hunt hints are re-measured while this screen is showing and auto-refresh is on. */
export const AUTO_REFRESH_MS = 45_000;

const nowSec = () => Math.floor(Date.now() / 1000);

/** Ticks every second: drives the scan cooldown and the hunts' time left, and hides a hunt as
 * soon as the device clock reaches its `expiresAt`. */
function useNow(): number {
  const [now, setNow] = useState(nowSec);
  useEffect(() => {
    const t = setInterval(() => setNow(nowSec()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function countdown(until: number, now: number): string {
  const s = Math.min(86400, Math.max(0, until - now));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
}

export function HuntScreen() {
  const now = useNow();
  const scan = useScan();
  const player = usePlayer((s) => s.player);
  const showToast = useShell((s) => s.showToast);
  const [open, setOpen] = useState<FindView | null>(null);
  const shown = useRef<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshingRef = useRef(false);

  // Open the find sheet whenever a new find lands.
  useEffect(() => {
    if (scan.find && scan.find.findId !== shown.current) {
      shown.current = scan.find.findId;
      setOpen(scan.find);
    }
  }, [scan.find]);

  const celebrate = (result: ResolveResult | null, find: FindView) => {
    setOpen(null);
    shown.current = null;
    if (!result) return;
    const color = rarityColors[find.rarity];
    if (result.kind === 'picked_up') {
      showToast(
        result.item.assembled ? `🧩✨ Assembled! ${rarityLabel(find.rarity)} item complete!` : `${'✨'.repeat(rarityWeight(find.rarity))} Added to your collection!`,
        { color, durationMs: (2 + rarityWeight(find.rarity)) * 1000 },
      );
    } else if (result.kind === 'part_collected') {
      showToast(`🧩 Part ${result.hunt.collected.length}/${result.hunt.totalParts} collected. Keep hunting!`, { color, durationMs: 3000 });
    } else {
      showToast('Left behind. Maybe someone else will find it.', { durationMs: 3000 });
    }
  };

  /** Re-measure every hunt's hints from where the player is now. No scan, no cooldown. */
  const refreshLocations = async (silent = false) => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    try {
      await usePlayer.getState().refreshLocation();
    } catch (e) {
      if (!silent) {
        showToast(
          e instanceof ApiException && e.isAccuracyTooLow
            ? POOR_SIGNAL_MESSAGE
            : e instanceof ApiException
              ? e.message
              : 'Could not refresh locations. Check your connection and GPS.',
          { durationMs: 3000 },
        );
      }
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  };

  // Quietly re-measure the hints while the player can see them: a hunt is open, the page
  // is visible and the Hunt tab is selected (tabs stay mounted when hidden).
  const hasHunts = activeHunts(player?.hunts ?? [], now).length > 0;
  const refreshRef = useRef(refreshLocations);
  refreshRef.current = refreshLocations;
  useEffect(() => {
    if (!autoRefresh || !hasHunts) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && useShell.getState().tab === 'hunt') void refreshRef.current(true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, hasHunts]);

  const cooldown = cooldownRemaining(scan.cooldownUntil, now);
  const canScan = !isBusy(scan) && cooldown === 0;
  const scanning = scan.phase === 'scanning';
  const hunts = activeHunts(player?.hunts ?? [], now);

  return (
    <section className="screen" aria-label="Hunt">
      <h1 className="app-bar">EthrHunt</h1>
      <div className="scroll">
        {player && <p className="t-title-l t-center" style={{ marginBottom: 16 }}>Hi {player.profile.displayName} 👋</p>}
        <div className="scan-wrap">
          <button type="button" className={`scan-btn${scanning ? ' scanning' : ''}`} disabled={!canScan} onClick={() => void scan.scan()} data-testid="scan-button" aria-label="Scan">
            {scanning && <span className="ring" />}
            <span className="inner">
              <span className="icon" aria-hidden="true">📡</span>
              <span className="label">{scanning ? 'Scanning…' : cooldown > 0 ? `${cooldown}s` : 'SCAN'}</span>
            </span>
          </button>
        </div>
        <div style={{ marginTop: 24 }}>
          <ResultArea now={now} />
        </div>
        <DropsSection now={now} />
        {hunts.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h2 className="t-title-l">Active hunts</h2>
              <label className="t-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Auto-refresh
                <input type="checkbox" role="switch" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="auto-refresh" />
              </label>
            </div>
            <div className="stack" style={{ display: 'grid', gap: 12 }}>
              {hunts.map((h) => (
                <HuntBubble key={h.itemId} hunt={h} item={catalog.get(h.itemId)} now={now} onRefreshLocations={() => void refreshLocations()} refreshing={refreshing} />
              ))}
            </div>
          </div>
        )}
      </div>
      {open && <FindResultSheet find={open} item={catalog.get(open.itemId)} onDone={(r) => celebrate(r, open)} />}
    </section>
  );
}

function ResultArea({ now }: { now: number }) {
  const scan = useScan();
  switch (scan.phase) {
    case 'idle':
    case 'scanning':
    case 'resolving':
      if (scan.lastResolve == null && scan.phase === 'idle') {
        return (
          <Bubble color={withAlpha(colors.sky, 0.25)} style={{ textAlign: 'center' }}>
            Tap Scan to see what the Ethr has left nearby. Walk a block or two to search a new spot.
          </Bubble>
        );
      }
      return null;
    case 'found':
      return (
        <Bubble color={withAlpha(colors.lemon, 0.6)} style={{ textAlign: 'center' }}>
          <span className="t-title-l">Something is here! ✨</span>
        </Bubble>
      );
    case 'nothing': {
      const searched = scan.nothingReason === 'alreadySearched';
      return (
        <Bubble style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 44 }} aria-hidden="true">{searched ? '🕳️' : '🍃'}</div>
          <div className="t-title-l" style={{ margin: '8px 0 6px' }}>{searched ? 'You already searched here' : 'Nothing here right now'}</div>
          {scan.nextWindowAt != null && <p>This spot refreshes in {countdown(scan.nextWindowAt, now)}</p>}
          <p>…or walk somewhere new.</p>
        </Bubble>
      );
    }
    case 'error':
      return (
        <Bubble color="#f9dedc" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40 }} aria-hidden="true">😵‍💫</div>
          <p role="alert" style={{ marginTop: 8 }}>{scan.errorMessage ?? 'Something went wrong.'}</p>
        </Bubble>
      );
  }
}

/** What other players (or this one) left lying within reach of the last scan. */
/** "Dropped here (N)": the live drops from the last scan, each with its time left. */
function DropsSection({ now }: { now: number }) {
  const drops = liveDrops(useScan((s) => s.drops), now);
  const pickingUp = useScan((s) => s.pickingUp);
  const showToast = useShell((s) => s.showToast);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  if (drops.length === 0) return null;

  const pickup = async (ids: string[]) => {
    setPending(new Set(ids));
    const outcome = await useScan.getState().pickupDrops(ids);
    setPending(new Set());
    if (!outcome) return;
    if ('error' in outcome) showToast(outcome.error, { durationMs: 3000 });
    else showToast(pickupMessage(outcome.pickedUp, outcome.unavailable), { durationMs: 3000 });
  };

  return (
    <div style={{ marginTop: 24 }} data-testid="drops-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <h2 className="t-title-l">Dropped here ({drops.length})</h2>
        <button type="button" className="btn filled" style={{ padding: '10px 18px', fontSize: 16 }} disabled={pickingUp} onClick={() => void pickup(drops.map((d) => d.dropId))}>
          {pickingUp && pending.size > 1 ? <span className="spinner" role="progressbar" aria-label="Picking up" /> : 'Pick up all'}
        </button>
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        {drops.map((d) => (
          <DropRow key={d.dropId} drop={d} now={now} disabled={pickingUp} busy={pickingUp && pending.size === 1 && pending.has(d.dropId)} onPickup={() => void pickup([d.dropId])} />
        ))}
      </div>
    </div>
  );
}

function DropRow({ drop, now, disabled, busy, onPickup }: { drop: DropView; now: number; disabled: boolean; busy: boolean; onPickup: () => void }) {
  const item = catalog.get(drop.itemId);
  return (
    <Bubble small color={withAlpha(item.palette, 0.35)} border={`3px solid ${rarityColors[drop.rarity]}`} testId={`drop-${drop.dropId}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ItemArt item={item} style={{ fontSize: 44, lineHeight: 1, flex: 'none' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-title-m">{item.name}{drop.assembled && <span aria-label="assembled"> 🧩</span>}</div>
          <div style={{ margin: '4px 0' }}><RarityBadge rarity={drop.rarity} compact /></div>
          <div className="t-small">{drop.mine ? 'You dropped this' : `Dropped by ${drop.droppedBy}`}</div>
          <div className="t-small"><TimeLeftLabel expiresAt={drop.expiresAt} now={now} /></div>
        </div>
        <button type="button" className="btn tonal" style={{ padding: '10px 16px', fontSize: 16, flex: 'none' }} disabled={disabled} onClick={onPickup} aria-label={`Pick up ${item.name}`}>
          {busy ? <span className="spinner dark" style={{ width: 20, height: 20, borderWidth: 3 }} role="progressbar" aria-label="Picking up" /> : 'Pick up'}
        </button>
      </div>
    </Bubble>
  );
}
